// SearchService.js
import axios from 'axios';
import { createHeaders } from './headerUtils';
import {
	CURSOR_PAGINATION_CONFIG,
	CursorPaginationUtils,
} from '../config/paginationConfig';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Polling settings for the async searches CSV export. The backend gives up on a
 * job after 15 minutes, so the client backstop sits just past that — a poll that
 * runs longer than the server's own cap would be waiting on a job the server has
 * already marked errored.
 */
const EXPORT_POLL_CONFIG = {
	INITIAL_INTERVAL_MS: 1500,
	MAX_INTERVAL_MS: 5000,
	// Each interval grows by this factor, so a slow export is not polled every
	// 1.5s for a quarter of an hour.
	BACKOFF_FACTOR: 1.3,
	TIMEOUT_MS: 16 * 60 * 1000,
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The API reports failures as `{ status: 'fail', data: '<message>' }`, so the
 * useful text is nested one level deeper than axios's own message.
 */
const apiErrorMessage = (error) => {
	const payload = error?.response?.data;
	if (typeof payload?.data === 'string') return payload.data;
	if (typeof payload === 'string') return payload;
	return error?.message || 'Unknown error';
};

const SearchService = {
	validateSearch: async (companyid, searchid) => {
		try {
			const apiUrl = `${BASE_URL}/searches/${companyid}/validate/${searchid}`;
			const headers = createHeaders();
			const response = await axios.get(apiUrl, { headers });
			return response.data;
		} catch (error) {
			console.log('SearchService (ERROR): validateSearch ', error.message);
			throw error;
		}
	},
	/**
	 * One page of searches. `limit` is the page size and `sinceId` is the cursor
	 * (the `nextSinceId` from the previous page; omit it for the first page).
	 *
	 * Paging is opt-in on the backend: called with no options, this returns the
	 * complete list in one response and no `paging` object.
	 *
	 * Resolves to `{ status, data, paging: { limit, count, nextSinceId, hasMore } }`.
	 */
	getSearches: async (companyid, { limit, sinceId } = {}) => {
		try {
			const apiUrl = `${BASE_URL}/searches/${companyid}`;
			const headers = createHeaders();
			const params = {};
			if (limit !== undefined && limit !== null) {
				params.limit = CursorPaginationUtils.getValidLimit(limit);
			}
			if (sinceId !== undefined && sinceId !== null && sinceId !== '') {
				params.sinceId = sinceId;
			}
			const response = await axios.get(apiUrl, { headers, params });
			return response.data;
		} catch (error) {
			console.log('SearchService (ERROR): getSearches ', error.message);
			throw error;
		}
	},
	/**
	 * Walks every page of searches and returns them as one array, so callers that
	 * need the whole list (tables, exports, validation) get it without a single
	 * unbounded response.
	 *
	 * @param {string} companyid
	 * @param {object} [options]
	 * @param {number} [options.limit] Page size.
	 * @param {number} [options.maxPages] Stop after this many pages.
	 * @param {(page: object[], total: number) => void} [options.onPage] Called
	 *        after each page with that page's searches and the running total —
	 *        use it to report progress or render incrementally.
	 * @returns {Promise<{ data: object[], truncated: boolean }>} `truncated` is
	 *          true when the page limit was hit before the end of the list.
	 */
	getAllSearches: async (companyid, options = {}) => {
		const {
			limit = CURSOR_PAGINATION_CONFIG.DEFAULT_LIMIT,
			maxPages = CURSOR_PAGINATION_CONFIG.MAX_PAGES,
			onPage,
		} = options;

		const collected = [];
		let sinceId = null;

		for (let page = 0; page < maxPages; page += 1) {
			// eslint-disable-next-line no-await-in-loop
			const response = await SearchService.getSearches(companyid, { limit, sinceId });
			const items = Array.isArray(response?.data) ? response.data : [];
			collected.push(...items);

			if (onPage) {
				onPage(items, collected.length);
			}

			const paging = response?.paging;
			// No paging object means the backend served the full list in one go.
			if (!paging || !paging.hasMore || !paging.nextSinceId) {
				return { data: collected, truncated: false };
			}
			sinceId = paging.nextSinceId;
		}

		// Hit the backstop: flag it rather than presenting a partial list as complete.
		console.log(
			`SearchService: stopped paginating searches for company ${companyid} at ${maxPages} pages`
		);
		return { data: collected, truncated: true };
	},
	/**
	 * Starts a CSV export of every search on the company.
	 *
	 * Returns immediately — the file is built in the background. Poll
	 * `getSearchesExportStatus` with the returned jobId, then call
	 * `getSearchesExportDownload` once it is ready. Triggering while one of the
	 * caller's own exports is still running returns that job (`reused: true`)
	 * rather than starting a second.
	 *
	 * Resolves to `{ status, data: { jobId, status, reused } }`.
	 */
	startSearchesExport: async (companyid) => {
		try {
			const apiUrl = `${BASE_URL}/searches/${companyid}/export`;
			const headers = createHeaders();
			const response = await axios.post(apiUrl, {}, { headers });
			return response.data;
		} catch (error) {
			console.log('SearchService (ERROR): startSearchesExport ', error.message);
			throw error;
		}
	},
	/**
	 * Status of an export job.
	 *
	 * Resolves to `{ status, data: { jobId, status, ready, failed, error,
	 * searchCount, fileName, truncated, warning } }`, where the inner `status` is
	 * one of initial | running | done | errored.
	 */
	getSearchesExportStatus: async (companyid, jobId) => {
		try {
			const apiUrl = `${BASE_URL}/searches/${companyid}/export/${jobId}`;
			const headers = createHeaders();
			const response = await axios.get(apiUrl, { headers });
			return response.data;
		} catch (error) {
			console.log('SearchService (ERROR): getSearchesExportStatus ', error.message);
			throw error;
		}
	},
	/**
	 * Short-lived presigned link to a finished export.
	 *
	 * Navigate to the returned `downloadUrl` (e.g. `window.location.assign`) so
	 * the browser downloads it natively — fetching it into a blob would pull the
	 * whole file (up to ~90 MB) through the tab, which is exactly what the
	 * presigned link exists to avoid. The link expires after `expiresInSeconds`.
	 *
	 * Resolves to `{ status, data: { downloadUrl, fileName, expiresInSeconds,
	 * searchCount, truncated, warning } }`. Errors with 409 while the job is
	 * still running or has failed, and 404 when it produced no file.
	 */
	getSearchesExportDownload: async (companyid, jobId) => {
		try {
			const apiUrl = `${BASE_URL}/searches/${companyid}/export/${jobId}/download`;
			const headers = createHeaders();
			const response = await axios.get(apiUrl, { headers });
			return response.data;
		} catch (error) {
			console.log('SearchService (ERROR): getSearchesExportDownload ', error.message);
			throw error;
		}
	},
	/**
	 * Runs the whole export: trigger, poll until the file is built, then fetch the
	 * download link. One call for the common case, so callers do not each
	 * reimplement the three-step dance.
	 *
	 * @param {string} companyid
	 * @param {object} [options]
	 * @param {(update: object) => void} [options.onStatus] Called with each poll
	 *        result (plus `{ jobId, elapsedMs }`) so the UI can report progress.
	 * @param {number} [options.pollIntervalMs] First gap between polls; grows up
	 *        to MAX_INTERVAL_MS.
	 * @param {number} [options.timeoutMs] Client-side backstop.
	 * @param {AbortSignal} [options.signal] Abort to stop polling. The export
	 *        itself keeps running on the server.
	 * @returns {Promise<object>} The download payload, or `{ empty: true,
	 *          searchCount: 0 }` when the company has no searches — a finished
	 *          export with no file, not a failure.
	 * @throws {Error} When the job fails, times out, or polling is aborted.
	 */
	runSearchesExport: async (companyid, options = {}) => {
		const {
			onStatus,
			pollIntervalMs = EXPORT_POLL_CONFIG.INITIAL_INTERVAL_MS,
			timeoutMs = EXPORT_POLL_CONFIG.TIMEOUT_MS,
			signal,
		} = options;

		const throwIfAborted = () => {
			if (signal?.aborted) {
				throw new Error('Export cancelled');
			}
		};

		throwIfAborted();

		let jobId;
		try {
			const started = await SearchService.startSearchesExport(companyid);
			jobId = started?.data?.jobId;
		} catch (error) {
			throw new Error(`Could not start the export: ${apiErrorMessage(error)}`);
		}
		if (!jobId) {
			throw new Error('The export did not return a jobId.');
		}

		const startedAt = Date.now();
		let interval = pollIntervalMs;

		for (;;) {
			throwIfAborted();
			// eslint-disable-next-line no-await-in-loop
			await delay(interval);
			throwIfAborted();
			interval = Math.min(
				Math.round(interval * EXPORT_POLL_CONFIG.BACKOFF_FACTOR),
				EXPORT_POLL_CONFIG.MAX_INTERVAL_MS
			);

			let job;
			try {
				// eslint-disable-next-line no-await-in-loop
				const response = await SearchService.getSearchesExportStatus(companyid, jobId);
				job = response?.data;
			} catch (error) {
				throw new Error(`Could not check the export: ${apiErrorMessage(error)}`);
			}

			const elapsedMs = Date.now() - startedAt;
			if (onStatus) {
				onStatus({ ...job, jobId, elapsedMs });
			}

			if (job?.failed) {
				throw new Error(job.error || 'The export failed.');
			}

			if (job?.ready) {
				// A finished export with no file means the company has no searches.
				// Asking for the download would 404, so report the empty result.
				if (!job.fileName) {
					return { empty: true, searchCount: job.searchCount ?? 0, jobId };
				}
				try {
					// eslint-disable-next-line no-await-in-loop
					const download = await SearchService.getSearchesExportDownload(companyid, jobId);
					return { ...download?.data, jobId };
				} catch (error) {
					throw new Error(`Could not get the download link: ${apiErrorMessage(error)}`);
				}
			}

			if (elapsedMs > timeoutMs) {
				throw new Error(
					'The export is taking longer than expected. It may still finish — try again in a few minutes.'
				);
			}
		}
	},
	getDeletedSearches: async (companyid) => {
		try {
			const apiUrl = `${BASE_URL}/searches/${companyid}/deletedsearches`;
			const headers = createHeaders();
			const response = await axios.get(apiUrl, { headers });
			return response.data;
		} catch (error) {
			console.log('SearchService (ERROR): getDeletedSearches ', error.message);
			throw error;
		}
	},
	restoreDeletedSearches: async (companyid, searches) => {
		try {
			const apiUrl = `${BASE_URL}/searches/${companyid}/restoresearches`;
			const headers = createHeaders();
			const response = await axios.post(apiUrl, searches, { headers });
			return response.data;
		} catch (error) {
			console.log('SearchService (ERROR): restoreDeletedSearches ', error.message);
			throw error;
		}
	},
	getFilters: async (companyid) => {
		try {
			const apiUrl = `${BASE_URL}/searches/${companyid}/filter`;
			const headers = createHeaders();
			const response = await axios.get(apiUrl, { headers });
			return response.data;
		} catch (error) {
			console.log('SearchService (ERROR): getFilters ', error.message);
			throw error;
		}
	},
	getCustomCats: async (companyid) => {
		try {
			const apiUrl = `${BASE_URL}/searches/${companyid}/customcats`;
			const headers = createHeaders();
			const response = await axios.get(apiUrl, { headers });
			return response.data;
		} catch (error) {
			console.log('SearchService (ERROR): getCustomCats ', error.message);
			throw error;
		}
	},
	getCustomCat: async (companyid, catid) => {
		try {
			const apiUrl = `${BASE_URL}/searches/${companyid}/customcats/${catid}`;
			const headers = createHeaders();
			const response = await axios.get(apiUrl, { headers });
			return response.data;
		} catch (error) {
			console.log('SearchService (ERROR): getCustomCat ', error.message);
			throw error;
		}
	},
	getFilterById: async (companyId, filterId) => {
		try {
			const apiUrl = `${BASE_URL}/searches/${companyId}/filter/${filterId}`;
			const headers = createHeaders();
			const response = await axios.get(apiUrl, { headers });
			return response.data;
		} catch (error) {
			console.log('SearchService (ERROR): getFilterById ', error.message);
			throw error;
		}
	},
	getDocex: async (companyid, quidID) => {
		try {
			const apiUrl = `${BASE_URL}/searches/${companyid}/getDocByQID/${quidID}`;
			const headers = createHeaders();
			const response = await axios.get(apiUrl, { headers });
			return response.data;
		} catch (error) {
			console.log('SearchService (ERROR): getDocex ', error.message);
			throw error;
		}
	},
	getProgressBar: async (companyid) => {
		try {
			const apiUrl = `${BASE_URL}/searches/${companyid}/customcats/progress`;
			const headers = createHeaders();
			const response = await axios.get(apiUrl, { headers });
			return response.data;
		} catch (error) {
			console.log('SearchService (ERROR): getProgressBar ', error.message);
			throw error;
		}
	},
	gethashDiffPercentage: (hash1, hash2) => {
		// Ensure that both hashes have the same length
		const maxLength = Math.max(hash1.length, hash2.length);
		const paddedHash1 = hash1.padStart(maxLength, '0');
		const paddedHash2 = hash2.padStart(maxLength, '0');

		// Count differing hexadecimal digits
		let differingDigits = 0;
		for (let i = 0; i < maxLength; i++) {
			if (paddedHash1[i] !== paddedHash2[i]) {
				differingDigits++;
			}
		}

		// Calculate percentage of differing digits
		const totalDigits = maxLength;
		const percentageDiff = (1.0 - differingDigits / totalDigits) * 100;

		return percentageDiff.toFixed(0);
	},
	gethashDiffPercentageBinary: (hash1, hash2) => {
		const bytesHash1 = atob(hash1);
		const bytesHash2 = atob(hash2);

		if ( bytesHash1.length !== bytesHash2.length ) {
			return null;
		}

		let distance = 0;
		const totalBits = bytesHash1.length * 8;

		for ( let i = 0; i < bytesHash1.length; i++ ) {
			let xor = bytesHash1.charCodeAt(i) ^ bytesHash2.charCodeAt(i);

			while ( xor > 0 ) {
			if ( xor & 1 ) { 
				distance++; 
			}
			xor >>= 1;
			}
		}

		const similarity = ( ( totalBits - distance ) / totalBits ) * 100;
		return similarity.toFixed(0);
	},
};

export default SearchService;
